import type { Terrain } from "@divespots/contracts";

/**
 * Fixtures mode only (E2E builds): the contracts terrain fixture has its shore along the north edge.
 * Turns the tile about its centre so the land lies towards `headingDeg` (degrees clockwise from
 * north; 0 = unchanged), nearest-neighbour, the edge cells extended. Lets the 3D view be tested on
 * differently angled shores without a contract change.
 */
export function rotateTerrain(t: Terrain, headingDeg: number): Terrain {
  if (headingDeg % 360 === 0) return t;
  const { width, height } = t.meta;
  const a = (headingDeg * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const cx = (width - 1) / 2;
  const cz = (height - 1) / 2;
  const elevation = new Float32Array(t.elevation.length);
  const source = new Uint8Array(t.source.length);
  let min = Infinity;
  let max = -Infinity;
  for (let r = 0; r < height; r++) {
    for (let c = 0; c < width; c++) {
      const x = c - cx;
      const z = r - cz;
      // the inverse of the turn: where this cell comes from in the original tile
      const sc = Math.min(width - 1, Math.max(0, Math.round(cx + x * cos + z * sin)));
      const sr = Math.min(height - 1, Math.max(0, Math.round(cz - x * sin + z * cos)));
      const j = sr * width + sc;
      const i = r * width + c;
      elevation[i] = t.elevation[j] as number;
      source[i] = t.source[j] as number;
      const v = elevation[i] as number;
      if (Number.isFinite(v)) {
        min = Math.min(min, v);
        max = Math.max(max, v);
      }
    }
  }
  return { meta: { ...t.meta, minElevM: min, maxElevM: max }, elevation, source };
}
